// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

// eslint-disable-next-line spire/fenced-imports
import { OffsetOptions } from "@floating-ui/dom";

import FloutingUiMenu, { MenuItem } from "@stoneage/App/Components/Menu/DropdownMenu";
import useMenuLinkClick from "@stoneage/MobiusOverrides/Menu/useMenuLinkClick";
import { isSamePagePath, replaceLocationHash, scrollPageToTop } from "@stoneage/XDL/Utilities";
import { MegaMenuStyle, RenderMegaMenuItems } from "@stoneage/MobiusOverrides/Menu/MegaMenu";
import { useId } from "@floating-ui/react";
import Clickable, { ClickablePresentationProps, ClickableProps } from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { IconMemo, IconPresentationProps } from "@insite/mobius/Icon";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import applyPropBuilder from "@insite/mobius/utilities/applyPropBuilder";
import getColor from "@insite/mobius/utilities/getColor";
import getProp from "@insite/mobius/utilities/getProp";
import InjectableCss, { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import MobiusStyledComponentProps from "@insite/mobius/utilities/MobiusStyledComponentProps";
import React, { FC, memo, useCallback } from "react";
import styled, { ThemeProps, useTheme } from "styled-components";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import { xClassNames } from "@stoneage/XDL/Utilities";
import { RenderWideMegaMenuItems, WideMegaMenuStyle } from "@stoneage/MobiusOverrides/Menu/WideMegaMenu";

export type MenuVariants = "primary" | "secondary";

const DEFAULT_BOUNDARY_SELECTOR = "#mainNavigationContainer > div";

// XNG-Changes: promoData shape threaded from L1 down to RenderMegaMenuItems
interface MenuPromoData {
    navigationImage?: string;
    navigationContent?: string;
    /** XNG-Changes: url of the item that owns this panel, used by the "View All" deep link. */
    parentUrl?: string;
}

export interface MappedLink {
    // expanded from content-library/src/widgets/header/MainNavigation.tsx
    title: string;
    url?: string;
    type?: string;
    clickableProps?: ClickableProps;
    openInNewWindow?: boolean;
    numberOfColumns?: number;
    maxDepth?: number;
    childrenType?: "MegaMenu" | "CascadingMenu" | "CustomLinkList";
    children?: MappedLink[];
    excludeFromNavigation?: boolean;
    iconProps?: IconPresentationProps;
    // XNG-Changes
    categoryId?: string;
    isApplication?: boolean;
    isNonClickable?: boolean;
    hasDivider?: boolean;
    properties?: {
        hidefromNavigation?: string;
        /** XNG-Changes: which mega-menu column this L2 item belongs to; defaults to "Column 1" */
        navigationColumn?: "Column 1" | "Column 2";
        navigationMaxLinks?: number;
        navigationImage?: string;
        navigationContent?: string;
    };
}

export interface MenuPresentationProps {
    /**
     * Indicates how the `css` property is combined with the variant `css` property from the theme.
     * If true, the variant css is applied first and then the component css is applied after causing
     * a merge, much like normal CSS. If false, only the component css is applied, overriding the variant css in the theme.
     */
    mergeCss?: boolean;
    /** CSS string or styled-components function to be injected into this component
     * @themable */
    cssOverrides?: {
        wrapper?: StyledProp<MenuPresentationProps>;
        menu?: StyledProp<MenuPresentationProps>;
        menuItem?: StyledProp<MenuPresentationProps>;
    };
    /** Props passed to the menu items.
     * @themable */
    menuItemTypographyProps?: TypographyPresentationProps;
    /** Props passed to the menu item Clickable.
     * @themable */
    menuItemClickableProps?: ClickablePresentationProps;
    /** Props to be passed to the right chevron arrow for menu items with children.
     * @themable */
    moreIconProps?: IconPresentationProps;
}

type MenuComponentProps = MobiusStyledComponentProps<
    "nav",
    {
        /** Aria attributes used internally require an `id`. If not provided, a random id is generated. */
        descriptionId?: string;
        /** Maximum number of menus that will appear through submenu cascading. */
        maxDepth?: number;
        /** The nested child structure that will be rendered by the menu. */
        menuItems: MappedLink[];
        /** The element that triggers the menu. */
        menuTrigger: React.ReactElement<{ id: string; className: string }>;
        /**
         * @deprecated Use CSS style to control width.
         * Width of the menu and submenus in pixels.
         */
        width?: number;
        /** Allows forcing the state of this menu to be open */
        isOpen?: true;
        /** Session boolean value that decides whether to show Change Customer Link */
        displayChangeCustomerLink?: boolean;
        /**
         * XNG-Changes
         * Choose 'Primary' when the subMenu needs to match the parent menu height.
         */
        menuVariant?: MenuVariants;
        /**
         * menu item index
         */
        referenceIndex?: number;
        offset?: OffsetOptions;
        /**
         * XNG-Changes
         */
        isMegaMenu?: boolean;
        /**
         * XNG-Changes
         */
        isWideMegaMenu?: boolean;
    }
>;

/** MenuComponentProps with defaulted properties now listed as not-optional, plus theme. */
export type MenuProps = MenuComponentProps & MenuPresentationProps;

type MenuStyleProps = ThemeProps<BaseTheme> &
    InjectableCss & {
        width?: number;
        /**
         * XNG-Changes
         */
        referenceIndex?: number;
        currentDepth?: number;
    };

const MenuStyle = styled.ul<MenuStyleProps>`
    display: flex;
    flex-direction: column;
    background: ${getColor("common.white")};
    z-index: ${getProp("theme.zIndex.menu")};
    width: ${getProp("width", 240)}px;
    height: 100%;
    list-style: none;
    margin: 0;
    padding: 0;

    & > .MenuItem[data-open] {
        background: ${getColor("primary")};
    }

    ${injectCss}
`;

type MenuItemStyleProps = ThemeProps<BaseTheme> &
    InjectableCss & {
        width?: number;
        onClick?: () => void;
    };

const MenuItemStyle = styled.li<MenuItemStyleProps>`
    position: relative;
    a {
        width: 100%;
        justify-content: space-between;
        &:hover {
            background: ${getColor("common.border")};
        }
    }
    ${injectCss}
`;

const MenuItemText = styled(Typography as any)<{ hasChildren?: boolean }>`
    padding: 0;
    color: ${getColor("common.black")};
    display: block;
    box-sizing: border-box;
    transition: all ${getProp("theme.transition.duration.regular", "300")}ms ease-in-out;

    &.non-clickable:hover {
        color: ${getColor("primary")};
    }
    &.application {
        cursor: pointer;
    }
`;

const RenderMenuItems: FC<{
    childMenuItems: MappedLink[];
    currentDepth: number;
    maxDepth: number;
    width?: number;
    applyProp: any;
    applyStyledProp: any;
    resolvedMergeCss: boolean | undefined;
    displayChangeCustomerLink?: boolean;
    // XNG-Changes: promoData added to signature so L1 promo is forwarded into the mega-menu panel
    className?: string;
    renderMenuItems: (items: MappedLink[], depth: number, promoData?: MenuPromoData) => React.ReactNode;
    menuVariant?: MenuVariants;
}> = memo(
    ({
        childMenuItems,
        currentDepth,
        maxDepth,
        width,
        applyProp,
        applyStyledProp,
        resolvedMergeCss = true,
        displayChangeCustomerLink,
        renderMenuItems,
        menuVariant,
        className,
    }) => {
        const id = useId();
        const history = useHistory();
        const handleMenuLinkClick = useMenuLinkClick();

        /**
         * XNG-Changes: the "application"/non-clickable items navigate imperatively,
         * so they need the same same-page handling as the anchor based items.
         */
        const handleApplicationClick = (url?: string) => {
            if (!url) {
                return;
            }

            if (isSamePagePath(url)) {
                replaceLocationHash();
                scrollPageToTop();
                return;
            }

            history.push(url);
        };

        return (
            <React.Fragment key={id}>
                {childMenuItems
                    ?.filter(item => !item.excludeFromNavigation)
                    .map((item, index) => {
                        const hasChildren =
                            item.children?.some(o => !o.excludeFromNavigation) && currentDepth < maxDepth;

                        if (item.type === "ChangeCustomerPage" && !displayChangeCustomerLink) {
                            return null;
                        }

                        const itemElement = (
                            <MenuItem
                                className={className}
                                targetComponent={
                                    <MenuItemStyle
                                        width={width}
                                        css={applyStyledProp(["cssOverrides", "menuItem"], resolvedMergeCss)}
                                        key={`${item.title}_${index}`}
                                        data-test-selector="menuItem"
                                    >
                                        {item.isNonClickable || item.isApplication ? (
                                            <MenuItemText
                                                className={xClassNames.append(
                                                    item.isApplication && "application",
                                                    "non-clickable",
                                                )}
                                                hasChildren={hasChildren}
                                                {...applyProp("menuItemTypographyProps")}
                                                data-test-selector="menuItemText"
                                                onClick={() => handleApplicationClick(item.url)}
                                            >
                                                {item.title}
                                            </MenuItemText>
                                        ) : (
                                            <Clickable
                                                href={item.url}
                                                target={item.openInNewWindow ? "_blank" : ""}
                                                {...applyProp("menuItemClickableProps")}
                                                {...item.clickableProps}
                                                // XNG-Changes: same-page links scroll back to the top
                                                // instead of doing nothing, and close the menu.
                                                onClick={(event: React.MouseEvent<HTMLElement>) => {
                                                    item.clickableProps?.onClick?.(event);
                                                    handleMenuLinkClick(event, {
                                                        href: item.url,
                                                        openInNewWindow: item.openInNewWindow,
                                                    });
                                                }}
                                                aria-label={item.title}
                                            >
                                                {item.iconProps && <IconMemo {...item.iconProps} />}
                                                <MenuItemText
                                                    hasChildren={hasChildren}
                                                    {...applyProp("menuItemTypographyProps")}
                                                    data-test-selector="menuItemText"
                                                >
                                                    {item.title}
                                                </MenuItemText>
                                                {hasChildren && <IconMemo {...applyProp("moreIconProps")} />}
                                            </Clickable>
                                        )}
                                    </MenuItemStyle>
                                }
                            />
                        );

                        if (hasChildren) {
                            return (
                                <FloutingUiMenu
                                    key={`FloutingUiMenu_${item.title}_${index}`}
                                    menuTrigger={itemElement}
                                    offset={{ mainAxis: -1, crossAxis: 0 }}
                                    placement="right-start"
                                    shift={{
                                        boundary:
                                            typeof document !== "undefined"
                                                ? document.querySelector(DEFAULT_BOUNDARY_SELECTOR) || document.body
                                                : "clippingAncestors",
                                        padding: 16,
                                    }}
                                    variant={menuVariant}
                                >
                                    {/*
                                     * XNG-Changes: pass this L1 item's promo fields so the mega-menu
                                     * panel can render the category image and content.
                                     */}
                                    {renderMenuItems(item!.children!, currentDepth + 1, {
                                        navigationImage: item.properties?.navigationImage,
                                        navigationContent: item.properties?.navigationContent,
                                        parentUrl: item.url,
                                    })}
                                </FloutingUiMenu>
                            );
                        }

                        return <React.Fragment key={`MenuItem_${item.title}_${id}`}>{itemElement}</React.Fragment>;
                    })}
            </React.Fragment>
        );
    },
);

const Menu: FC<MenuProps> = ({
    menuItems,
    menuTrigger,
    isOpen,
    mergeCss,
    menuVariant,
    referenceIndex,
    offset,
    isWideMegaMenu,
    ...otherProps
}) => {
    const theme = useTheme() as BaseTheme;
    const { applyProp, applyStyledProp } = applyPropBuilder({ ...otherProps, theme }, { component: "menu" });
    const resolvedMergeCss = mergeCss ?? theme?.menu?.defaultProps?.mergeCss;
    const { maxDepth = 3, width, displayChangeCustomerLink, isMegaMenu } = otherProps;

    // XNG-Changes: promoData parameter added — carries navigationImage / navigationContent from the
    // L1 item (passed in RenderMenuItems above) into the mega-menu promo panel.
    const renderMenuItems = useCallback(
        (childMenuItems: MappedLink[], currentDepth: number, promoData?: MenuPromoData) => {
            if (currentDepth === 1 && isMegaMenu && isWideMegaMenu) {
                return (
                    <WideMegaMenuStyle
                        referenceIndex={referenceIndex}
                        currentDepth={currentDepth}
                        className="mega-menu"
                    >
                        <RenderWideMegaMenuItems childMenuItems={childMenuItems} />
                    </WideMegaMenuStyle>
                );
            }

            if (currentDepth === 2 && isMegaMenu) {
                return (
                    <MegaMenuStyle referenceIndex={referenceIndex} currentDepth={currentDepth} className="mega-menu">
                        <RenderMegaMenuItems
                            childMenuItems={childMenuItems}
                            navigationImage={promoData?.navigationImage}
                            navigationContent={promoData?.navigationContent}
                            parentUrl={promoData?.parentUrl}
                        />
                    </MegaMenuStyle>
                );
            }

            return (
                <MenuStyle
                    width={otherProps.width}
                    referenceIndex={referenceIndex}
                    currentDepth={currentDepth}
                    className={isMegaMenu ? "mega-menu" : "cascading-menu"}
                    css={applyStyledProp(["cssOverrides", "menu"], resolvedMergeCss)}
                >
                    <RenderMenuItems
                        childMenuItems={childMenuItems}
                        currentDepth={currentDepth}
                        width={width}
                        maxDepth={maxDepth}
                        applyProp={applyProp}
                        applyStyledProp={applyStyledProp}
                        resolvedMergeCss={resolvedMergeCss}
                        displayChangeCustomerLink={displayChangeCustomerLink}
                        renderMenuItems={renderMenuItems}
                        menuVariant={menuVariant}
                        className={isMegaMenu ? "mega-menu" : "cascading-menu"}
                    />
                </MenuStyle>
            );
        },
        [applyStyledProp, resolvedMergeCss, width, displayChangeCustomerLink, maxDepth],
    );

    return (
        <FloutingUiMenu
            menuTrigger={menuTrigger}
            overlay={true}
            offset={offset || { mainAxis: 5 }}
            placement="bottom-start"
            shift={{
                boundary:
                    typeof document !== "undefined"
                        ? document.querySelector(DEFAULT_BOUNDARY_SELECTOR) || document.body
                        : "clippingAncestors",
                padding: 16,
            }}
            variant={menuVariant}
        >
            {renderMenuItems(menuItems, 1)}
        </FloutingUiMenu>
    );
};

/** @component */
export default Menu;

export { MenuItemText };
